import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, MapPin, Loader2, PartyPopper } from "lucide-react";

/**
 * PublicCalendarSection — the calendar block that appears on `/vendors/{slug}`.
 *
 * Shows only entries the profile owner has flipped `public_on_calendar=true`.
 * Private-booking entries come back redacted from the API (`is_redacted=true`)
 * — we render them as an anonymous "Booked" pill so the date signals
 * availability without leaking the party details.
 *
 * Renders nothing at all when the owner hasn't made anything public — no
 * point in a big empty block on their profile.
 */
export default function PublicCalendarSection({ slug, displayName }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/users/by-slug/${slug}/calendar`);
        if (alive) setItems(data.items || []);
      } catch { if (alive) setItems([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <section className="card-warm rounded-sm p-5 sm:p-6 mt-4"
               data-testid="public-calendar-loading">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--avt-purple)" }} />
      </section>
    );
  }
  // Owner hasn't marked anything public — hide the section entirely.
  if (!items || items.length === 0) return null;

  return (
    <section
      className="card-warm rounded-sm p-5 sm:p-6 mt-4"
      data-testid="public-calendar-section"
    >
      <div className="overline mb-2" style={{ color: "var(--avt-purple)" }}>
        Upcoming schedule
      </div>
      <h2 className="font-serif text-2xl mb-1">Catch {displayName} in person.</h2>
      <p className="text-sm mb-4" style={{ color: "var(--ink-soft)" }}>
        Dates {displayName} has chosen to share publicly.
      </p>
      <ul className="divide-y" style={{ borderColor: "var(--border-warm)" }}>
        {items.map((it) => (
          <li key={`${it.kind}:${it.source_id}`}>
            <PublicRow entry={it} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PublicRow({ entry }) {
  const dayNum = Number(entry.date.slice(8, 10));
  const dayLabel = new Date(entry.date).toLocaleDateString(undefined,
    { weekday: "short", month: "short" });
  const isRedacted = entry.is_redacted;
  return (
    <div
      className="flex items-start gap-4 py-3"
      data-testid={`public-calendar-row-${entry.kind}-${entry.source_id}`}
    >
      <div className="text-center px-2" style={{ minWidth: 56 }}>
        <div className="text-[10px] uppercase tracking-widest"
             style={{ color: "var(--ink-soft)" }}>{dayLabel}</div>
        <div className="font-serif text-2xl leading-none"
             style={{ color: "var(--avt-purple)" }}>{dayNum}</div>
      </div>
      <div className="flex-1 min-w-0">
        {isRedacted ? (
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold"
               style={{ color: "var(--avt-pink)" }}>
            <PartyPopper className="h-3.5 w-3.5" />
            Booked
          </div>
        ) : (
          <>
            <div className="font-semibold truncate">
              {entry.url ? (
                <Link to={entry.url} className="hover:underline">{entry.title}</Link>
              ) : entry.title}
            </div>
            {(entry.city || entry.location) && (
              <div className="text-xs mt-0.5 flex items-center gap-1"
                   style={{ color: "var(--ink-soft)" }}>
                <MapPin className="h-3 w-3" /> {entry.city || entry.location}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
